<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <button class="btn btn-link text-muted px-0 mb-2" (click)="goBack()">
          <i class="bi bi-arrow-left me-1"></i>
          Back to EPA Chemicals
        </button>
        <div class="d-flex align-items-center">
          <h1 class="h2 mb-1 fw-bold">{{ getDisplayName() || 'EPA Chemical Detail' }}</h1>
          <span *ngIf="chemical?.isHidden" class="badge bg-secondary ms-3">
            <i class="bi bi-eye-slash me-1"></i>
            Hidden
          </span>
        </div>
        <small class="text-muted" *ngIf="chemical?.epaNumber as epaNum">
          EPA Registration: <code>{{ epaNum }}</code>
        </small>
      </div>
    </div>

    <!-- Error/Success Messages -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Content -->
    <ng-container *ngIf="!loading && chemical as chem">
      <div class="row g-4">
        <!-- Left Column: EPA Data (Read-only) -->
        <div class="col-lg-8">
          <!-- Latest Label Card -->
          <ng-container *ngIf="getCurrentLabel() as currentLabel">
            <div class="card mb-4">
              <div class="card-header bg-primary">
                <h5 class="mb-0 text-white">
                  <i class="bi bi-file-earmark-pdf me-2 text-white"></i>
                  {{ currentLabel.isCurrent ? 'Current Label' : 'Latest Label' }}
                </h5>
              </div>
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <a
                    [href]="currentLabel.labelUrl"
                    target="_blank"
                    class="btn btn-outline-primary me-3"
                  >
                    <i class="bi bi-file-earmark-pdf me-2"></i>
                    View Label PDF
                  </a>
                  <div>
                    <div *ngIf="currentLabel.labelName" class="text-muted small">
                      {{ currentLabel.labelName }}
                    </div>
                    <div class="text-muted small">
                      Accepted: {{ formatDate(currentLabel.acceptedDate) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- Basic Information Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>EPA Registration Information</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label text-muted">EPA Number</label>
                  <div class="form-control-plaintext py-0"><code>{{ chem.epaNumber || '-' }}</code></div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">Product Name</label>
                  <div class="form-control-plaintext py-0 fw-medium">{{ chem.name }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">Manufacturer</label>
                  <div class="form-control-plaintext py-0">{{ chem.manufacturer || '-' }}</div>
                </div>
              </div>

              <hr>

              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label text-muted">Status</label>
                  <div class="form-control-plaintext py-0">
                    <span *ngIf="chem.status" class="badge" [ngClass]="getStatusClass(chem.status)">
                      {{ chem.status }}
                    </span>
                    <span *ngIf="!chem.status">-</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">Signal Word</label>
                  <div class="form-control-plaintext py-0">
                    <span *ngIf="chem.signalWord" class="badge" [ngClass]="getSignalWordClass(chem.signalWord)">
                      {{ chem.signalWord }}
                    </span>
                    <span *ngIf="!chem.signalWord">-</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">Restricted Use (RUP)</label>
                  <div class="form-control-plaintext py-0">
                    <span *ngIf="chem.rupFlag" class="badge bg-danger">
                      <i class="bi bi-exclamation-triangle me-1"></i>
                      Yes - Restricted
                    </span>
                    <span *ngIf="!chem.rupFlag" class="text-muted">No</span>
                  </div>
                </div>
              </div>

              <div *ngIf="chem.rupReason" class="mt-3">
                <label class="form-label text-muted">RUP Reason</label>
                <div class="form-control-plaintext bg-danger-subtle p-2 rounded">{{ chem.rupReason }}</div>
              </div>

              <hr>

              <div class="row g-3">
                <div class="col-md-12">
                  <label class="form-label text-muted">Category</label>
                  <div class="form-control-plaintext py-0">{{ chem.category || '-' }}</div>
                </div>
              </div>

              <hr>

              <div class="row g-3">
                <div class="col-md-12">
                  <label class="form-label text-muted">Active Ingredient</label>
                  <div class="form-control-plaintext py-0">{{ chem.activeIngredient || '-' }}</div>
                </div>
              </div>

              <div *ngIf="chem.formulation" class="mt-3">
                <label class="form-label text-muted">Formulation</label>
                <div class="form-control-plaintext py-0">{{ chem.formulation }}</div>
              </div>
            </div>
          </div>

          <!-- Sites/Crops & Target Pests Card -->
          <div class="card mb-4" *ngIf="chem.sites || chem.pests">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Sites & Pests</h5>
            </div>
            <div class="card-body">
              <div *ngIf="chem.sites" class="mb-3">
                <label class="form-label text-muted">Sites / Crops</label>
                <div class="form-control-plaintext py-0">
                  <div class="d-flex flex-wrap gap-1">
                    <span *ngFor="let site of formatSemicolonList(chem.sites)" class="badge bg-success-subtle text-success-emphasis">
                      {{ site }}
                    </span>
                  </div>
                </div>
              </div>

              <div *ngIf="chem.pests">
                <label class="form-label text-muted">Target Pests</label>
                <div class="form-control-plaintext py-0">
                  <div class="d-flex flex-wrap gap-1">
                    <span *ngFor="let pest of formatSemicolonList(chem.pests)" class="badge bg-danger-subtle text-danger-emphasis">
                      {{ pest }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional EPA Details Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-list-ul me-2"></i>Additional EPA Details</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label text-muted">Registration Type</label>
                  <div class="form-control-plaintext py-0">{{ chem.registrationType || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">Company Number</label>
                  <div class="form-control-plaintext py-0">{{ chem.companyNumber || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">First Registration</label>
                  <div class="form-control-plaintext py-0">{{ formatDate(chem.firstRegistrationDate) }}</div>
                </div>
              </div>

              <div class="row g-3 mt-2">
                <div class="col-md-4">
                  <label class="form-label text-muted">Use Type</label>
                  <div class="form-control-plaintext py-0">{{ chem.useType || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">EPA Team</label>
                  <div class="form-control-plaintext py-0">{{ chem.epaTeam || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted">PM Email</label>
                  <div class="form-control-plaintext py-0">
                    <a *ngIf="chem.pmEmail" [href]="'mailto:' + chem.pmEmail">{{ chem.pmEmail }}</a>
                    <span *ngIf="!chem.pmEmail">-</span>
                  </div>
                </div>
              </div>

              <div *ngIf="chem.statusDescription" class="mt-3">
                <label class="form-label text-muted">Status Description</label>
                <div class="form-control-plaintext py-0">{{ chem.statusDescription }}</div>
              </div>

              <div *ngIf="chem.epaAltBrandNames" class="mt-3">
                <label class="form-label text-muted">EPA Alternate Brand Names</label>
                <div class="form-control-plaintext py-0">{{ chem.epaAltBrandNames }}</div>
              </div>

              <div *ngIf="chem.usePattern" class="mt-3">
                <label class="form-label text-muted">Use Pattern</label>
                <div class="form-control-plaintext py-0">{{ chem.usePattern }}</div>
              </div>

              <div *ngIf="chem.transferHistory" class="mt-3">
                <label class="form-label text-muted">Transfer History</label>
                <div class="form-control-plaintext py-0">{{ chem.transferHistory }}</div>
              </div>
            </div>
          </div>

          <!-- Labels Management Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-file-earmark-pdf me-2"></i>Labels ({{ chem.labels?.length || 0 }})</h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="toggleAddLabelForm()"
              >
                <i class="bi" [class.bi-plus]="!showAddLabelForm" [class.bi-x]="showAddLabelForm"></i>
                {{ showAddLabelForm ? 'Cancel' : 'Add Label' }}
              </button>
            </div>
            <div class="card-body" *ngIf="showAddLabelForm">
              <div class="p-3 rounded mb-3" style="background-color: var(--bg-tertiary, #f8f9fa);">
                <div class="mb-2">
                  <label class="form-label small">Label URL <span class="text-danger">*</span></label>
                  <input
                    type="url"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelUrl"
                    placeholder="https://example.com/label.pdf"
                  />
                </div>
                <div class="mb-2">
                  <label class="form-label small">Label Name</label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelName"
                    placeholder="e.g., Product Label v2.1"
                  />
                </div>
                <div class="mb-2">
                  <label class="form-label small">Accepted Date</label>
                  <input
                    type="date"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelDate"
                  />
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="setLabelCurrent"
                      [(ngModel)]="newLabelSetCurrent"
                    />
                    <label class="form-check-label small" for="setLabelCurrent">
                      Set as current label
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-success w-100"
                  (click)="addLabel()"
                  [disabled]="addingLabel || !newLabelUrl.trim()"
                >
                  <span *ngIf="addingLabel" class="spinner-border spinner-border-sm me-1"></span>
                  Add Label
                </button>
              </div>
            </div>
            <div *ngIf="!chem.labels || chem.labels.length === 0" class="card-body text-center text-muted py-4">
              <i class="bi bi-file-earmark-pdf d-block mb-2" style="font-size: 2rem;"></i>
              No labels added yet. Click "Add Label" to add one.
            </div>
            <div class="table-responsive" *ngIf="chem.labels && chem.labels.length > 0">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Accepted Date</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let label of chem.labels">
                    <td>{{ label.labelName || 'Label PDF' }}</td>
                    <td>{{ formatDate(label.acceptedDate) }}</td>
                    <td>
                      <span *ngIf="label.isCurrent" class="badge bg-success">Current</span>
                      <span *ngIf="!label.isCurrent" class="text-muted">Historical</span>
                    </td>
                    <td class="text-end">
                      <div class="btn-group btn-group-sm">
                        <a [href]="label.labelUrl" target="_blank" class="btn btn-outline-primary" title="View">
                          <i class="bi bi-box-arrow-up-right"></i>
                        </a>
                        <button
                          type="button"
                          [class]="label.isCurrent ? 'btn btn-success' : 'btn btn-outline-success'"
                          [title]="label.isCurrent ? 'Unmark as current' : 'Set as current'"
                          (click)="setCurrentLabel(label)"
                          [disabled]="settingCurrentLabelId === label.id"
                        >
                          <span *ngIf="settingCurrentLabelId === label.id" class="spinner-border spinner-border-sm"></span>
                          <i *ngIf="settingCurrentLabelId !== label.id" class="bi" [class.bi-check-circle-fill]="label.isCurrent" [class.bi-check-circle]="!label.isCurrent"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-danger"
                          title="Delete label"
                          (click)="deleteLabel(label)"
                          [disabled]="deletingLabelId === label.id"
                        >
                          <span *ngIf="deletingLabelId === label.id" class="spinner-border spinner-border-sm"></span>
                          <i *ngIf="deletingLabelId !== label.id" class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Sync Information Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-cloud-arrow-down me-2"></i>Sync Information</h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="syncFromPpls()"
                [disabled]="syncingPpls"
                title="Fetch latest data from EPA PPLS API"
              >
                <span *ngIf="syncingPpls" class="spinner-border spinner-border-sm me-1"></span>
                <i *ngIf="!syncingPpls" class="bi bi-arrow-repeat me-1"></i>
                Sync from PPLS
              </button>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label text-muted">Last PPIS Bulk Sync</label>
                  <div class="form-control-plaintext py-0">{{ formatDateTime(chem.epaPpisSyncedAt) }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label text-muted">Last PPLS API Sync</label>
                  <div class="form-control-plaintext py-0">{{ formatDateTime(chem.epaPplsSyncedAt) }}</div>
                </div>
              </div>
              <div class="row g-3 mt-2">
                <div class="col-md-6">
                  <label class="form-label text-muted">Created At</label>
                  <div class="form-control-plaintext py-0">{{ formatDateTime(chem.createdAt) }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label text-muted">Last Updated</label>
                  <div class="form-control-plaintext py-0">{{ formatDateTime(chem.updatedAt) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Admin Fields & Custom Names -->
        <div class="col-lg-4">
          <!-- Admin Fields Card -->
          <div class="card mb-4 admin-fields-card">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-pencil-square me-2"></i>Admin Fields</h5>
            </div>
            <div class="card-body">
              <form [formGroup]="adminForm" (ngSubmit)="saveAdminFields()">
                <div class="mb-3">
                  <label class="form-label">Safety Data Sheet URL</label>
                  <input
                    type="url"
                    class="form-control"
                    formControlName="sdsUrl"
                    placeholder="https://example.com/sds.pdf"
                  />
                  <small class="form-text text-muted">Link to manufacturer's SDS document</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">Admin Notes</label>
                  <textarea
                    class="form-control"
                    formControlName="adminNotes"
                    rows="3"
                    placeholder="Internal notes about this chemical..."
                  ></textarea>
                </div>

                <div class="mb-3">
                  <div class="form-check form-switch">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="isHidden"
                      formControlName="isHidden"
                    />
                    <label class="form-check-label" for="isHidden">
                      <strong>Hide from Users</strong>
                      <small class="d-block text-muted">Hidden chemicals won't appear in user searches</small>
                    </label>
                  </div>
                </div>

                <button type="submit" class="btn btn-primary w-100" [disabled]="saving">
                  <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                  Save Admin Fields
                </button>
              </form>
            </div>
          </div>

          <!-- Custom Names Card -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-tags me-2"></i>Custom Names</h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="toggleAddNameForm()"
              >
                <i class="bi" [class.bi-plus]="!showAddNameForm" [class.bi-x]="showAddNameForm"></i>
              </button>
            </div>
            <div class="card-body">
              <!-- Add Name Form -->
              <div *ngIf="showAddNameForm" class="mb-3 p-3 rounded border bg-body-tertiary">
                <div class="mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Enter custom name..."
                    [(ngModel)]="newCustomName"
                  />
                </div>
                <div class="mb-2">
                  <select class="form-select form-select-sm" [(ngModel)]="newCustomNameType">
                    <option *ngFor="let opt of nameTypeOptions" [value]="opt.value">{{ opt.label }}</option>
                  </select>
                </div>
                <div class="mb-2">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="isPrimary"
                      [(ngModel)]="newCustomNamePrimary"
                    />
                    <label class="form-check-label small" for="isPrimary">
                      Set as primary display name
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-success w-100"
                  (click)="addCustomName()"
                  [disabled]="addingName || !newCustomName.trim()"
                >
                  <span *ngIf="addingName" class="spinner-border spinner-border-sm me-1"></span>
                  Add Name
                </button>
              </div>

              <!-- Custom Names List -->
              <div *ngIf="!chem.customNames || chem.customNames.length === 0" class="text-muted text-center py-3">
                <i class="bi bi-tags d-block mb-2" style="font-size: 2rem;"></i>
                No custom names added yet
              </div>

              <ul *ngIf="chem.customNames && chem.customNames.length > 0" class="list-group list-group-flush">
                <li
                  *ngFor="let customName of chem.customNames"
                  class="list-group-item bg-transparent d-flex justify-content-between align-items-start"
                >
                  <div>
                    <div class="fw-medium">
                      {{ customName.name }}
                      <span *ngIf="customName.isPrimary" class="badge bg-primary ms-1">Primary</span>
                    </div>
                    <small class="text-muted">{{ getNameTypeLabel(customName.nameType) }}</small>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    (click)="removeCustomName(customName)"
                    title="Remove"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Quick Info Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-lightning me-2"></i>Quick Info</h5>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Source</span>
                <span class="badge bg-info">{{ chem.source }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">REI</span>
                <span>{{ chem.rei || '-' }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">PHI</span>
                <span>{{ chem.phi || '-' }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Default Rate</span>
                <span>{{ chem.defaultRate || '-' }}</span>
              </div>
              <div *ngIf="chem.maxLabelDate" class="d-flex justify-content-between">
                <span class="text-muted">Max Label Date</span>
                <span>{{ formatDate(chem.maxLabelDate) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>
</div>
